import React from 'react'
import moment from 'moment'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'

const round = (value) => Math.round((Number(value) || 0) * 100) / 100
const OVERTIME_SUFFIX = ':overtime'

export const isOvertimeRowId = (id) => String(id).endsWith(OVERTIME_SUFFIX)
const shiftIdOf = (id) => String(id).replace(OVERTIME_SUFFIX, '')

// Rows for the "link shifts to an invoice" grids. A shift's base pay and its
// overtime claim are separate rows: pending overtime stays visible but can't
// be ticked, and approved overtime can go on a later invoice once the claim
// has been decided. Anything already on a live invoice gets no row at all.
// An unverified shift stays visible but can't be ticked either - the server
// rejects unverified shifts on invoices.
export const buildInvoiceRows = (lines = []) =>
  lines.flatMap((line) => {
    const shift = String(line.shift)
    const baseInvoiced = !!(line.staffInvoice || line.baseInvoiced)
    const overtimeInvoiced = !!(line.overtimeInvoice || line.overtimeInvoiced)
    // Only an explicit false disables the row; lines from older servers
    // carry no flag at all and stay selectable
    const isVerified = line.isVerified !== false
    const rows = []

    if (!baseInvoiced) {
      rows.push({
        id: shift,
        kind: 'shift',
        shift,
        start: line.start,
        description: line.description,
        clinicalRole: line.clinicalRole,
        hours: line.hours,
        amount: round(
          (line.basePay ?? 0) + (line.bonusPay ?? 0) + (line.travelPay ?? 0),
        ),
        isVerified,
        selectable: isVerified,
      })
    }

    const overtime = line.overtime
    if (overtime && overtime.status !== 'Rejected' && !overtimeInvoiced) {
      rows.push({
        id: `${shift}${OVERTIME_SUFFIX}`,
        kind: 'overtime',
        shift,
        start: line.start,
        description: line.description,
        clinicalRole: line.clinicalRole,
        hours: round(overtime.minutes / 60),
        amount: overtime.pay,
        overtimeStatus: overtime.status,
        baseInvoiced,
        isVerified,
        selectable: overtime.status === 'Approved' && isVerified,
      })
    }

    return rows
  })

export const splitInvoiceSelection = (selection = []) => ({
  shifts: selection.filter((id) => !isOvertimeRowId(id)).map(String),
  overtimeShifts: selection.filter(isOvertimeRowId).map(shiftIdOf),
})

// Overtime can only be ticked with its shift, or once the shift is already
// on an invoice
export const isInvoiceRowSelectable = (row, selection = []) =>
  row.selectable &&
  (row.kind !== 'overtime' ||
    row.baseInvoiced ||
    selection.some((id) => String(id) === row.shift))

// Unticking a shift also unticks its overtime row
export const normaliseInvoiceSelection = (rows, selection = []) => {
  const ids = selection.map(String)
  return ids.filter((id) => {
    const row = rows.find((r) => r.id === id)
    return row && isInvoiceRowSelectable(row, ids)
  })
}

export const selectionTotal = (rows, selection = []) => {
  const ids = new Set(selection.map(String))
  return round(
    rows
      .filter((row) => ids.has(row.id))
      .reduce((sum, row) => sum + row.amount, 0),
  )
}

// VAT on top of a net selection total for a VAT-registered worker. Rounds
// each part the way the server's priceInvoiceRequest does, so the gross
// matches the exact-amount check to the penny.
export const invoiceTotalsWithVat = (net, rate = 0) => {
  const netRounded = round(net)
  const vat = round(netRounded * (Number(rate) || 0))
  return { net: netRounded, vat, gross: round(netRounded + vat) }
}

export const selectionLabel = (selection = []) => {
  const { shifts, overtimeShifts } = splitInvoiceSelection(selection)
  const parts = [`${shifts.length} ${shifts.length === 1 ? 'shift' : 'shifts'}`]
  if (overtimeShifts.length) parts.push(`${overtimeShifts.length} overtime`)
  return `${parts.join(' · ')} selected`
}

const OVERTIME_CHIP = {
  Pending: { label: 'Overtime pending', color: 'warning', variant: 'filled' },
  Approved: {
    label: 'Overtime approved',
    color: 'success',
    variant: 'outlined',
  },
  Rejected: {
    label: 'Overtime rejected',
    color: 'default',
    variant: 'outlined',
  },
}

// A shift's overtime claim state; an approved claim shows what it adds
export const OvertimeChip = ({ overtime, format }) => {
  if (!overtime) return null
  const chip = OVERTIME_CHIP[overtime.status] ?? OVERTIME_CHIP.Pending
  return (
    <Chip
      size="small"
      color={chip.color}
      variant={chip.variant}
      label={
        overtime.status === 'Approved' && format
          ? `${chip.label} · ${format(overtime.pay)}`
          : chip.label
      }
      sx={{ borderRadius: 1 }}
    />
  )
}

// Status of an overtime picker row (grid cell and detail popup)
export const OvertimeStatusChip = ({ status }) => (
  <Chip
    size="small"
    color={status === 'Approved' ? 'success' : 'warning'}
    variant={status === 'Approved' ? 'outlined' : 'filled'}
    label={status === 'Approved' ? 'Approved' : 'Awaiting approval'}
    sx={{ borderRadius: 1 }}
  />
)

// Row class names for the pending-pay grid (one row per shift) and the
// invoice grids (separate overtime rows), paired with `overtimePendingRowSx`
export const pendingOvertimeRowClassName = ({ row }) =>
  row.overtime?.status === 'Pending' ? 'an-overtime-pending' : ''

export const invoiceRowClassName = ({ row }) =>
  row.kind === 'overtime' && row.overtimeStatus === 'Pending'
    ? 'an-overtime-pending'
    : ''

// Tint for rows carrying an undecided overtime claim
export const overtimePendingRowSx = {
  '& .an-overtime-pending': {
    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
  },
}

// Picker grids open a row's details on click, so show a pointer as well
export const invoiceGridSx = {
  ...overtimePendingRowSx,
  '& .MuiDataGrid-row': { cursor: 'pointer' },
}

export const invoiceRowColumns = (format) => [
  {
    field: 'start',
    headerName: 'Shift',
    minWidth: 170,
    valueGetter: (value) => moment(value).format('DD MMM YYYY HH:mm'),
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 240,
    flex: 1,
    renderCell: ({ row }) => (
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        height="100%"
        minWidth={0}
      >
        <Typography variant="body2" noWrap>
          {row.kind === 'overtime'
            ? `Overtime — ${row.description ?? ''}`
            : row.description}
        </Typography>
        {row.kind === 'overtime' && (
          <OvertimeStatusChip status={row.overtimeStatus} />
        )}
        {row.isVerified === false && (
          <Chip
            size="small"
            color="warning"
            variant="filled"
            label="Awaiting verification"
            sx={{ borderRadius: 1 }}
          />
        )}
      </Stack>
    ),
  },
  {
    field: 'clinicalRole',
    headerName: 'Role',
    minWidth: 130,
  },
  {
    field: 'hours',
    headerName: 'Hours',
    minWidth: 90,
  },
  {
    field: 'amount',
    headerName: 'Expected',
    minWidth: 120,
    valueGetter: (value) => format(value ?? 0),
  },
]

const Detail = ({ label, children }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    gap={2}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      component="div"
      variant="body2"
      fontWeight={600}
      textAlign="right"
    >
      {children}
    </Typography>
  </Stack>
)

// What a picker row holds, opened by clicking the row. Nothing here that the
// row doesn't already carry. Rendered once per page, outside the grid, so
// clicks inside it can't bubble back to a row.
export const InvoiceRowDialog = ({ row, onClose, format }) => {
  if (!row) return null
  const overtime = row.kind === 'overtime'
  const approved = row.overtimeStatus === 'Approved'

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>
        <Typography
          variant="overline"
          color="text.secondary"
          display="block"
          lineHeight={1.4}
        >
          {overtime ? 'Overtime claim' : 'Shift'}
        </Typography>
        {row.description || 'Shift'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={1.25}>
          <Detail label="Shift">
            {moment(row.start).format('ddd D MMM YYYY, HH:mm')}
          </Detail>
          <Detail label="Role">{row.clinicalRole || '—'}</Detail>
          <Detail label={overtime ? 'Overtime hours' : 'Hours'}>
            {row.hours}
          </Detail>
          {overtime && (
            <Detail label="Status">
              <OvertimeStatusChip status={row.overtimeStatus} />
            </Detail>
          )}
          <Divider />
          <Detail label="Expected">{format(row.amount ?? 0)}</Detail>
          {row.isVerified === false && (
            <Typography variant="caption" color="text.secondary">
              This shift is awaiting verification and can't be added to an
              invoice until payroll has verified it.
            </Typography>
          )}
          {overtime && (
            <Typography variant="caption" color="text.secondary">
              {!approved
                ? "This claim is awaiting a decision and can't be added to an invoice yet."
                : row.baseInvoiced
                  ? 'The shift itself is already on an invoice, so this overtime can be invoiced on its own.'
                  : 'Tick the shift first; this overtime can then be added to the same invoice.'}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
